import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  People,
  Work,
  Build,
  BuildCircle,
  Schedule,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Assignment,
  Message,
  Warning,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { mockApi } from '../../services/mockData';

interface DashboardStats {
  // Key Business Metrics
  newSales: {
    thisWeek: number;
    lastWeek: number;
    thisMonth: number;
    lastMonth: number;
    totalValue: number;
    trend: string;
  };
  scheduledJobs: {
    today: number;
    thisWeek: number;
    nextWeek: number;
    thisMonth: number;
    urgent: number;
    trend: string;
  };
  serviceRequests: {
    open: number;
    inProgress: number;
    completed: number;
    urgent: number;
    thisWeek: number;
    trend: string;
  };
  messages: {
    unread: number;
    today: number;
    thisWeek: number;
    urgent: number;
    fromCustomers: number;
    fromTeam: number;
    trend: string;
  };
  // Additional detailed metrics
  customers: {
    totalCustomers: number;
    activeCustomers: number;
    prospectCustomers: number;
    totalValue: number;
  };
  installations: {
    totalInstallations: number;
    completedInstallations: number;
    inProgressInstallations: number;
    scheduledInstallations: number;
  };
  schedules: {
    totalSchedules: number;
    completedSchedules: number;
    inProgressSchedules: number;
    scheduledSchedules: number;
  };
  services: {
    totalServiceRequests: number;
    openServiceRequests: number;
    inProgressServiceRequests: number;
    completedServiceRequests: number;
    cancelledServiceRequests: number;
    averageResolutionTime: number;
    averageCustomerSatisfaction: number;
  };
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const stats = await mockApi.getDashboard();
      setStats(stats);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!stats) return null;

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ReactNode;
    trend?: 'up' | 'down' | 'neutral';
    trendValue?: string;
    color?: string;
  }> = ({ title, value, subtitle, icon, trend, trendValue, color = '#D4AF37' }) => (
    <Card sx={{ 
      background: 'linear-gradient(145deg, #2C2C2C 0%, #3A3A3A 100%)',
      border: '1px solid #333333',
      '&:hover': {
        border: '1px solid #D4AF37',
        transform: 'translateY(-2px)',
        transition: 'all 0.3s ease',
      },
    }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography 
              gutterBottom 
              variant="h6"
              sx={{ color: '#FFFFFF' }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h4" 
              component="div"
              sx={{ 
                color: color,
                fontWeight: 700,
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#AAAAAA',
                  mt: 0.5,
                }}
              >
                {subtitle}
              </Typography>
            )}
            {trend && trendValue && (
              <Box display="flex" alignItems="center" mt={1}>
                {trend === 'up' ? (
                  <TrendingUp sx={{ color: '#4CAF50' }} fontSize="small" />
                ) : trend === 'down' ? (
                  <TrendingDown sx={{ color: '#F44336' }} fontSize="small" />
                ) : null}
                <Typography
                  variant="body2"
                  sx={{ 
                    ml: 0.5,
                    color: trend === 'up' ? '#4CAF50' : trend === 'down' ? '#F44336' : '#FFFFFF',
                  }}
                >
                  {trendValue}
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ color: '#D4AF37' }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

  const installationStatusData = [
    { name: 'Completed', value: stats.installations.completedInstallations, color: '#4caf50' },
    { name: 'In Progress', value: stats.installations.inProgressInstallations, color: '#ff9800' },
    { name: 'Scheduled', value: stats.installations.scheduledInstallations, color: '#2196f3' },
  ];

  const monthlyData = [
    { name: 'Jan', projects: 4, revenue: 24000 },
    { name: 'Feb', projects: 3, revenue: 18000 },
    { name: 'Mar', projects: 5, revenue: 30000 },
    { name: 'Apr', projects: 6, revenue: 36000 },
    { name: 'May', projects: 4, revenue: 24000 },
    { name: 'Jun', projects: 7, revenue: 42000 },
  ];

  return (
    <Box sx={{ backgroundColor: '#000000', minHeight: '100vh', p: 3 }}>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{
          color: '#D4AF37',
          fontWeight: 700,
          mb: 4,
        }}
      >
        ⚡ Dashboard
      </Typography>
      
      <Grid container spacing={3}>
        {/* Key Business Metrics */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="New Sales"
            value={stats.newSales.thisWeek}
            subtitle={`$${stats.newSales.totalValue.toLocaleString()} this week`}
            icon={<AttachMoney fontSize="large" />}
            trend="up"
            trendValue={stats.newSales.trend}
            color="#4CAF50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Scheduled Jobs"
            value={stats.scheduledJobs.today}
            subtitle={`${stats.scheduledJobs.urgent} urgent today`}
            icon={<Assignment fontSize="large" />}
            trend="up"
            trendValue={stats.scheduledJobs.trend}
            color="#2196F3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Service Requests"
            value={stats.serviceRequests.open + stats.serviceRequests.inProgress}
            subtitle={`${stats.serviceRequests.urgent} urgent`}
            icon={<BuildCircle fontSize="large" />}
            trend="down"
            trendValue={stats.serviceRequests.trend}
            color="#FF9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Messages"
            value={stats.messages.unread}
            subtitle={`${stats.messages.urgent} urgent`}
            icon={<Message fontSize="large" />}
            trend="up"
            trendValue={stats.messages.trend}
            color="#9C27B0"
          />
        </Grid>

        {/* Additional Metrics Row */}
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="Total Customers"
              value={stats.customers.totalCustomers}
              icon={<People fontSize="large" />}
              trend="up"
              trendValue="+12%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="Installs"
              value={stats.installations.scheduledInstallations}
              icon={<Build fontSize="large" />}
              trend="neutral"
              trendValue="0%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="This Week Jobs"
              value={stats.scheduledJobs.thisWeek}
              icon={<Schedule fontSize="large" />}
              trend="up"
              trendValue="+15%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              title="Completed Services"
              value={stats.serviceRequests.completed}
              icon={<BuildCircle fontSize="large" />}
              trend="down"
              trendValue="-8%"
            />
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Monthly Projects & Revenue
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Bar yAxisId="left" dataKey="projects" fill="#2196f3" name="Projects" />
                <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#4caf50" name="Revenue ($)" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Installation Status
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={installationStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {installationStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Projects
            </Typography>
            <Box>
              <Typography variant="body2" color="text.secondary">
                No recent projects to display
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Upcoming Installations
            </Typography>
            <Box>
              <Typography variant="body2" color="text.secondary">
                No upcoming installations to display
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
